// Auto-generated by splitter.py — do not regenerate; edit directly.

// UUID / uuidv7 (e.g. 019e5f32-fb3b-7000-ab66-e2a641bfff5c). Raw uuids in the UI
// read as machine noise — shorten them git-style. Human refs (ORG-2018, 7JL9GRA3R25,
// TXN-…) are left untouched.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A NAME A HUMAN TYPED OR — far more often — PASTED.
 *
 * Operators run this console next to WhatsApp. A club messages "new team account for Kibera
 * Soccer", somebody copies the name across, and what lands in the input is not what is on the
 * screen: WhatsApp (and Word, and Slack) carry invisible formatting characters through the
 * clipboard. Measured on fanpass — two rows, from two different surfaces, both pasted:
 *
 *     Team.name    " ⁠Kibera Soccer Women"   21 chars, 19 visible
 *     Venue.name   " ⁠Gusii Stadium, Kisii"  same shape
 *
 * `.trim()` does not help: U+2060 WORD JOINER is not whitespace, so trim removes the leading
 * space, stops at the joiner, and leaves a name that sorts before every other row, fails every
 * exact-match search and export join, and renders as a name with a mysterious gap in front of it.
 * The team is un-findable by typing its own name.
 *
 * So names are cleaned where they are WRITTEN, not audited afterwards: zero-width and joiner
 * characters removed, non-breaking spaces made ordinary, runs of whitespace collapsed, ends
 * trimmed. Nothing visible is changed — if the result differs from the input, the difference was
 * invisible by definition.
 */
const INVISIBLE_RE = /[​-‏‪-‮⁠-⁤﻿]/g;
export const cleanName = (value: string | null | undefined): string =>
  String(value ?? '')
    .replace(INVISIBLE_RE, '')
    .replace(/[   ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** A filename/URL-safe slug. Lived as three separate private copies (ExportFlowModal,
 * OrganizerDetailPage, NewEventWizard) before this; a fourth was about to be written for the
 * order add-ons download, which is the point at which a helper belongs in one place. `max`
 * exists because the event-slug caller caps at 72.
 *
 * Extracting it was only half the job: the copies stayed, so this accent fix would have reached
 * none of them. ExportFlowModal and NewEventWizard now call THIS. (OrganizerDetailPage's copy is
 * left alone — that module is imported by nothing and renders nowhere.) */
export const slugify = (value: string | number | null | undefined, { max, fallback = 'export' }: { max?: number; fallback?: string } = {}): string => {
  const s = String(value ?? '')
    .toLowerCase()
    .trim()
    // FOLD accents, don't drop them. `[^a-z0-9]` treats `í` as a separator, so
    // "WANGARÍ MAATHAI" became "wangar-maathai" — while the slug this event is actually
    // published under is "wangari-maathai". Generating a slug would have silently moved
    // the public URL. NFD splits the letter from its mark; the mark is what we discard.
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return (max ? s.slice(0, max).replace(/-+$/, '') : s) || fallback;
};

/** A clean, scannable short form of an id: LAST 8 chars of a UUID, uppercased
 * (uuidv7s share a timestamp prefix, so the tail is what distinguishes them);
 * human refs pass through as-is. Render in a font-mono span. */
export const shortId = (value: string | number | null | undefined): string => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  return UUID_RE.test(s) ? s.slice(-8).toLocaleUpperCase() : s;
};

/** True when the value is a raw UUID (so callers can decide whether to offer "copy full"). */
export const isUuid = (value: string | number | null | undefined): boolean =>
  UUID_RE.test(String(value ?? '').trim());

/** Mask an email for display: keep the first char + tld, star the rest
 * (nickson@gmail.com → n***@gmail.com). Enough to recognise your OWN inbox
 * without printing the full address in a toast. Returns '' for a blank input. */
export const maskEmail = (value: string | null | undefined): string => {
  const s = String(value ?? '').trim();
  const at = s.indexOf('@');
  if (at < 1) return s;                       // no local part → nothing to mask
  return `${s[0]}***${s.slice(at)}`;
};

/** Order codes are compact base32 (e.g. GP3KZV29M869). Split once after the first
 * 4 characters (GP3K-ZV29M869) — a short prefix to anchor on, then the rest in one
 * block — so they're scannable without over-chunking. Purely cosmetic: anything that
 * already carries separators — UUIDs, ORG-2018, TXN-… — or a non-alphanumeric value
 * passes through untouched, and the RAW (undashed) value is still what gets stored /
 * searched / copied. */
export const formatOrderId = (value: string | number | null | undefined): string => {
  const s = String(value ?? '').trim();
  if (!s || isUuid(s) || !/^[A-Za-z0-9]+$/.test(s) || s.length <= 4) return s;
  return `${s.slice(0, 4)}-${s.slice(4)}`;
};

export const fmtUGX = (n: number | null | undefined): string => {
  if (n === 0 || n) {
    // UGX (and the other shilling currencies here) have no minor unit — round to
    // whole shillings so amounts don't render with stray decimals (e.g. the
    // net_amount generated column produced 1,187,563.28 / 783.086).
    return new Intl.NumberFormat('en-UG', { maximumFractionDigits: 0 }).format(n);
  }
  return '—';
};

// Currencies with no minor unit — render whole units, no decimals.
const ZERO_DECIMAL_CURRENCIES = new Set(['UGX', 'KES', 'TZS', 'RWF', 'BIF', 'SSP', 'XAF', 'XOF', 'JPY', 'KRW', 'VND']);

/** Plain integer with thousands separators — for COUNTS (orders, tickets), never money.
 * (fmtUGX was being misused for counts; use this so counts never gain a currency prefix.) */
export const fmtNum = (n: number | null | undefined): string =>
  (n === 0 || n) ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n) : '—';

/** Compact amount for tight KPI tiles — "3.57B", "51.5M", "10K", "445". Number only
 * (pair with a separate currency chip). '—' when no value. */
export const fmtCompact = (n: number | null | undefined): string =>
  (n === 0 || n) ? new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(n) : '—';

/** Currency-aware money: "{CUR} {amount}". The currency MUST come from the data.
 * Shilling/zero-decimal currencies render whole units; others (USD, EUR…) two
 * decimals. '—' when there is no value.
 *
 * NO FALLBACK CURRENCY, and no bare number either. An amount without its currency
 * is not a partial truth, it is a different fact: 3,200 is ~USD 0.85 in UGX, ~USD 25
 * in KES and USD 3,200 in USD. So an unknown currency returns '—' — the amount is
 * withheld along with it, because the two are ONE value, not two fields.
 *
 * This previously read `currency || 'UGX'`, directly contradicting the line above it:
 * mookh order PD5PPQQ3ZGZ3 (KES ticket, buyer chose USD, payment pending) rendered a
 * confident UGX that appeared nowhere in the transaction. */
/** Format one {label, value, fmt} KPI tile from the report engine's summary
 * (`getReportSummary`'s `kpis` array — see reportExecutor.ts): 'money' compacts to
 * K/M/B, 'pct' adds a % sign, anything else is a plain localized number. Matches the
 * pattern already used inline in ReportBuilderModal.jsx / ReportDetailDrawer.jsx — the
 * ONE place for it, so a new summary renderer doesn't reinvent (or mis-invent) a copy.
 *
 * A MONEY KPI NOW CARRIES ITS CURRENCY, and this said the opposite ("currency-less by
 * design") only because the engine had none to give: the strip's underlying query added
 * every currency together, so there was no single code that would have been true. Now
 * that both KPI paths are bound to one book (reportExecutor: derivedSummary /
 * getReportSummary), the code travels with the number and is rendered when present —
 * the same rule as fmtMoney directly below. Absent, it degrades to the bare figure. */
export const fmtKpi = (k: { value: number | string | null; fmt?: string; signed?: boolean; currency?: string | null } | null | undefined): string => {
  if (!k) return '—';
  const v = k.value;
  // A NULL VALUE IS NOT ZERO. The engine emits null for "the question has no answer
  // here" — a period-over-period change with no previous period, a rate whose
  // denominator was 0. Both inline copies of this function did `Number(k.value) || 0`
  // and rendered a confident 0, which reads as "nothing changed" rather than "there is
  // nothing to compare against". Those two facts lead to opposite decisions.
  if (v == null || v === '' || (typeof v === 'number' && !Number.isFinite(v))) return '—';
  // 'percent' is a FRACTION from the engine (0.5368 = 54%); 'pct' is already a whole
  // number. Neither inline copy knew 'percent' at all, so every composed rate rendered
  // as "0.537" — a number no reader would recognise as a percentage.
  if (k.fmt === 'percent' && typeof v === 'number') {
    const pct = v * 100;
    const body = `${Math.abs(pct) > 0 && Math.abs(pct) < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
    return k.signed && pct > 0 ? `+${body}` : body;
  }
  if (k.fmt === 'pct') return `${v}%`;
  if (k.fmt === 'money' && typeof v === 'number') {
    const a = Math.abs(v), sign = v < 0 ? '-' : k.signed && v > 0 ? '+' : '';
    const cur = k.currency ? `${String(k.currency).toUpperCase()} ` : '';
    return cur + sign + (a >= 1e9 ? `${(a / 1e9).toFixed(1)}B` : a >= 1e6 ? `${(a / 1e6).toFixed(1)}M` : a >= 1e3 ? `${(a / 1e3).toFixed(1)}k` : a.toLocaleString());
  }
  return typeof v === 'number' ? v.toLocaleString() : String(v);
};

export const fmtMoney = (n: number | null | undefined, currency?: string | null): string => {
  if (n !== 0 && !n) return '—';
  const cur = currency ? String(currency).toUpperCase() : '';
  if (!cur) return '—';
  const digits = ZERO_DECIMAL_CURRENCIES.has(cur) ? 0 : 2;
  return `${cur} ${new Intl.NumberFormat('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n)}`;
};

/** Currency-aware COMPACT money: "{CUR} {compact}". Adapts magnitude across ALL
 * orders (K/M/B) via fmtCompact — so 10,450 → "UGX 10.45K" (NOT "UGX 0.0M") and
 * 3.44e9 → "UGX 3.44B". The single-magnitude "/1e6 → 0.0M" bug is structurally
 * impossible here. Currency MUST come from the data. '—' when no value. */
/** The AMOUNT only, with the decimal places that currency actually uses — no code.
 * For tables that state the currency once (a column header, a caption) instead of
 * repeating it on every row. Still currency-AWARE: it needs the code to know whether
 * 2 decimals are real money or noise, it just doesn't print it. Never call this
 * without a currency; that's what fmtMoney's '—' is for. */
export const fmtAmount = (n: number | null | undefined, currency?: string | null): string => {
  if (n !== 0 && !n) return '—';
  const cur = currency ? String(currency).toUpperCase() : '';
  const digits = ZERO_DECIMAL_CURRENCIES.has(cur) ? 0 : 2;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n);
};

export const fmtMoneyCompact = (n: number | null | undefined, currency?: string | null): string => {
  if (n !== 0 && !n) return '—';
  // Same rule as fmtMoney: no currency means no number. See its comment for why a bare
  // amount is a different fact rather than a partial one.
  const cur = currency ? String(currency).toUpperCase() : '';
  if (!cur) return '—';
  return `${cur} ${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 0 }).format(n)}`;
};

type SmartTimeOpts = { seconds?: boolean; fallback?: string };

// Day-aware time. Renders just the clock (HH:MM) for a timestamp that falls on
// TODAY, but prefixes the date for any other day — so a value from another day
// never reads as if it were today's time. Crosses years → includes the year.
/** A calendar day, said one way everywhere: "10 Jul 2026". `toLocaleDateString()` printed the browser's own order —
 *  10/07/2026 — which a reader cannot tell from 7 October. */
export const fmtDay = (value: string | number | Date | null | undefined, fallback = '—'): string => {
  if (value == null || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? fallback : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const fmtSmartTime = (value: string | number | Date | null | undefined, { seconds = false, fallback = '—' }: SmartTimeOpts = {}): string => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  const now = new Date();
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}) });
  const sameDay = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  if (sameDay) return time;
  const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) });
  return `${datePart}, ${time}`;
};

/**
 * HOW LONG AGO — the shape a headline tile can wear in every state.
 *
 * A count over a short window can only say something when the window happens to be busy. Measured
 * over 30 days, the MEDIAN selling event has an order in 1.9% of its 10-minute windows on prod
 * (0.9% moments, 1.2% mookhv3), so the console's "Recent buyers · active in a 10-min window" tile
 * read 0 on roughly 99 glances in 100 — including on the highest-volume event in the database. A
 * tile is read in EVERY state and cannot distinguish "quiet now" from "never sold" from "the
 * pipeline is dead", so a number that is almost always zero teaches an operator to stop reading
 * the strip it sits in.
 *
 * Recency is informative in every state — "2 min ago" during a rush, "6h ago" in a lull, and the
 * caller's own `never` when there is nothing to report. It also survives caching: this figure is
 * served from a 5-minute cache, which makes "3h ago" fine and made a 10-minute window
 * self-contradictory (up to half of it always stale).
 *
 * Deliberately coarse. Beyond a day nobody is counting hours, and a false precision like
 * "37h ago" reads as machine output rather than an answer.
 */
/** How many days "N ago" covers before a moment is said by its day instead (fmtAgo). */
export const AGO_DAYS = 7;
/** Whether fmtAgo still says this moment as "N ago" — for a place that shows the date beside it only then. */
export const saidAsAgo = (value: string | number | Date | null | undefined): boolean => {
  const t = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(t) && Date.now() - t < AGO_DAYS * 86400000;
};
const fmtDayOf = (d: Date): string => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }) });

export const fmtAgo = (
  value: string | number | Date | null | undefined,
  { never = 'never', suffix = ' ago' }: { never?: string; suffix?: string } = {},
): string => {
  if (!value) return never;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return never;
  const secs = Math.round((Date.now() - then) / 1000);
  // A clock skew or a just-written row can land microseconds in the future; that is "now", not
  // a negative duration.
  if (secs < 60) return `just now`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min${suffix}`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h${suffix}`;
  const days = Math.round(hours / 24);
  // A MOMENT PAST A WEEK IS PLACED BY ITS DAY. "216 days ago" makes the reader do the arithmetic to learn when; past a
  // week the date is the answer ("31 Mar", the year when not this one). A duration — `suffix: ''`, "waiting 216 days"
  // — is a different fact and stays a count.
  if (suffix && days >= AGO_DAYS) return fmtDayOf(new Date(then));
  if (days < 30) return `${days} day${days === 1 ? '' : 's'}${suffix}`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}${suffix}`;
  const years = Math.round(months / 12);
  return `${years} year${years === 1 ? '' : 's'}${suffix}`;
};

/**
 * IS THIS REFERENCE HOLDING MONEY THAT WAS NEVER CREDITED?
 *
 * ONE definition, exported, because this fact was being written four different ways — a server
 * predicate, a tab badge, a red dot and a detail banner — and they disagreed about the same row:
 * a reference could sit on the worklist and show no banner when opened, or show the banner and
 * not be on the worklist.
 *
 * It compares the two columns the settlement writer maintains, and NOT `expected`: an attempt with
 * no recorded expectation is not an attempt that expected zero (498 of 810 prod attempts have a
 * null expectedAmount, 265 of them settled), and receipt COUNT is not money at all — one MookhPay
 * callback legitimately carries several charges that were all credited.
 *
 * The epsilon matches the server's `+ 0.01`, so a sub-cent float residue cannot light a red alarm
 * over an empty tab.
 */
export const overpaidBy = (t: { amountReceived?: number | null; amountCredited?: number | null; fee?: number | null }): number | null => {
  if (t?.amountReceived == null || t?.amountCredited == null) return null;
  // Less the fee: it arrives with every gateway charge and is the platform's, never credited —
  // the server's predicate is `amountReceived > amountCredited + fee + 0.01`.
  const over = Number(t.amountReceived) - Number(t.amountCredited) - Number(t.fee || 0);
  return over > 0.01 ? over : null;
};
export const isOverpaid = (t: { amountReceived?: number | null; amountCredited?: number | null; fee?: number | null }): boolean =>
  overpaidBy(t) != null;

/**
 * WHAT AN ORDER HOLDS BEYOND ITS TOTAL: amountPaid − frozenTotal. The ORDER's twin of overpaidBy.
 *
 * overpaidBy asks of one reference "did a charge land that it didn't credit?". This asks of the
 * order "was more credited than it owed?" — and those are different holes. A buyer who taps Pay
 * twice gets two references, each charged once and each credited in full, so every reference reads
 * clean while the order holds double (moments G2AA8FLAN7GZ: 40,000 paid on a 20,000 total; 29 such
 * on moments in 180 days). It is not a plan fact either: it used to be read only off the
 * installment and split views, which are null for a one-shot order — the commonest place for it.
 *
 * Null when the order never committed a total (nothing to be over) or holds no more than it.
 */
export const orderOverpaidBy = (o: { amountPaid?: number | string | null; frozenTotal?: number | string | null; amountRefunded?: number | string | null } | null | undefined): number | null => {
  if (o?.frozenTotal == null || o?.amountPaid == null) return null;
  // Still owed back: paid past the total, less what has already gone back (the order's amountRefunded — a refund
  // returns the overpayment first). The server's Overpaid tab reads the same three columns.
  const over = Number(o.amountPaid) - Number(o.frozenTotal) - (Number(o.amountRefunded) || 0);
  return over >= 1 ? over : null;
};

/**
 * WHAT ONE CHARGE ASKED FOR: the order's share plus the buyer's transaction fee — what the rail was
 * actually asked to collect (backend askedOf). Null when no expectation was recorded.
 */
export const askedOf = (t: { expectedAmount?: number | null; fee?: number | null }): number | null =>
  t?.expectedAmount == null ? null : Number(t.expectedAmount) + Number(t.fee || 0);

/**
 * WHAT ONE CHARGE TOOK FROM THE BUYER, fee included — the figure on their SMS, and the one a column of charges can be
 * read down. A settled charge's `amount` is what reached the order, so its fee is added; an unsettled one is what the
 * rail was asked (askedOf). Lists showed "35,000 + 1,050 fee" on one row and "72,100 incl. 2,100 fee" on the next.
 */
export const chargedOf = (t: { amount?: number | null; status?: string; expectedAmount?: number | null; fee?: number | null }): number | null =>
  t?.amount == null ? askedOf(t) : Math.abs(Number(t.amount)) + (t.status === 'successful' ? Number(t.fee || 0) : 0);

// ─────────────────────────────────────────────────────────────────────────────
// ORGANISER-AUTHORED RICH TEXT → one line of readable text.
//
// Descriptions (ticket type, event, venue, season-pass tier) are authored on the
// organiser side and reach this console in THREE formats, because the editor changed
// twice and the stored rows were never normalised:
//
//   1. raw HTML      — mookh v2 rows, carried across verbatim by the migration
//                      (migrate-mookh-v2-to-destination.ts:933 passes ticket_description
//                      through as a string, while :750 converts the EVENT description).
//   2. Lexical JSON  — the retired organiser editor, `{"root":{"children":[…]}}`.
//   3. markdown      — current, since ~July 2026.
//
// Rendering `{value}` in JSX escapes whatever it is, so format 1 shows its tags on
// screen (`<p><br></p>…`) and format 3 shows its asterisks. Only the SYMPTOM differs:
// markdown degrades gracefully and HTML does not, which is why this looked like it
// affected only some rows.
//
// This strips rather than renders, deliberately. The alternative — dangerouslySetInnerHTML —
// would execute organiser-authored markup inside a console holding refund and payout
// controls; there is no innerHTML anywhere in this codebase and this is not the place to
// introduce it. Entities are decoded from a fixed table AFTER tags are removed, so nothing
// decoded can re-introduce markup.
//
// Fixing the stored data is a separate, riskier job (it rewrites organisers' own copy).
// Until that happens the console meets all three shapes, so it normalises on read.
// ─────────────────────────────────────────────────────────────────────────────

const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  hellip: '…', mdash: '—', ndash: '–', rsquo: '’', lsquo: '‘',
  rdquo: '”', ldquo: '“', middot: '·', bull: '•',
};

const decodeEntities = (s: string): string =>
  s.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (whole, body: string) => {
    if (body[0] === '#') {
      const cp = body[1]?.toLowerCase() === 'x'
        ? parseInt(body.slice(2), 16)
        : parseInt(body.slice(1), 10);
      // Ignore anything that isn't a sane codepoint rather than emitting U+FFFD.
      return Number.isFinite(cp) && cp > 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : whole;
    }
    return ENTITIES[body.toLowerCase()] ?? whole;
  });

/** Walk a Lexical tree and collect its text nodes in order. */
const lexicalText = (node: any): string => {
  if (!node || typeof node !== 'object') return '';
  if (node.type === 'text' && typeof node.text === 'string') return node.text;
  if (node.type === 'linebreak') return ' ';
  const kids = Array.isArray(node.children) ? node.children.map(lexicalText).join(' ') : '';
  // Block-level nodes need a separator or words weld together across paragraphs.
  return node.type === 'paragraph' || node.type === 'heading' || node.type === 'listitem'
    ? `${kids} `
    : kids;
};

/** Shared core: any of the three formats → text with a NEWLINE at every block boundary.
 *  Callers decide whether those breaks survive (a reading surface) or collapse (a cell). */
const toText = (value: unknown): string => {
  if (value == null) return '';
  let s = String(value);

  if (s.startsWith('{') && s.includes('"root"')) {
    try {
      const tree = JSON.parse(s);
      if (tree?.root) s = lexicalText(tree.root);
    } catch { /* not the JSON we hoped for — fall through and treat it as text */ }
  }

  s = s
    // Block boundaries become newlines FIRST, so "…flights.</p><p>No payment" cannot
    // weld into "…flights.No payment" once the tags are gone.
    .replace(/<(br|\/p|\/div|\/li|\/h[1-6]|\/tr|\/blockquote)\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '');

  return decodeEntities(s)
    // Markdown emphasis/heading marks are noise once nothing will render them.
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__|[*_`])/g, '');
};

/** ONE LINE, for table cells, labels and tooltips. Pass `max` to ellipsise. */
export const plainText = (value: unknown, max?: number): string => {
  const s = toText(value).replace(/\s+/g, ' ').trim();
  if (max && s.length > max) return `${s.slice(0, max - 1).trimEnd()}…`;
  return s;
};

/** PARAGRAPHS PRESERVED, for surfaces whose job is reading the description. Render with
 *  `whitespace-pre-line`; collapsing this to one line would turn a structured description
 *  into a wall of text, which is a different kind of broken from the one being fixed. */
export const plainTextBlock = (value: unknown): string =>
  toText(value)
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')   // editors emit <p><br></p> runs; one blank line is enough
    .trim();

/**
 * A TIME BUCKET, AS A HUMAN WOULD SAY IT.
 *
 * The engine labels buckets with ISO keys — `2026-W35`, `2026-08`, `2026-08-27` — because a
 * label that sorts chronologically is what makes an axis an axis. That is right on the wire
 * and wrong on the screen: nobody knows which week `2026-W35` is, and a column of them is
 * unreadable precisely where a reader most needs to see a trend.
 *
 * So the label is translated at the LAST possible moment, and only here. `this week` and
 * `last week` where they apply, because that is what a person actually calls them; a date
 * range otherwise, because "week 35" is not a thing anyone can picture. Anything unrecognised
 * is returned untouched — this must never invent a reading for a label it does not understand.
 *
 * `now` is a parameter so the same input always renders the same output in a test.
 */
export const prettyBucket = (label: string | number | null | undefined, now: Date = new Date()): string => {
  const s = String(label ?? '');
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ── ISO week: 2026-W35 ──────────────────────────────────────────────────
  const wk = s.match(/^(\d{4})-W(\d{2})$/);
  if (wk) {
    const [, yy, ww] = wk;
    // ISO-8601: week 1 is the week containing 4 January; weeks start Monday.
    const jan4 = new Date(Date.UTC(Number(yy), 0, 4));
    const isoMonday = new Date(jan4);
    isoMonday.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (Number(ww) - 1) * 7);
    const end = new Date(isoMonday); end.setUTCDate(isoMonday.getUTCDate() + 6);

    const thisMonday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    thisMonday.setUTCDate(thisMonday.getUTCDate() - ((thisMonday.getUTCDay() + 6) % 7));
    const weeksAgo = Math.round((+thisMonday - +isoMonday) / (7 * 86400000));
    if (weeksAgo === 0) return 'This week';
    if (weeksAgo === 1) return 'Last week';

    const sameMonth = isoMonday.getUTCMonth() === end.getUTCMonth();
    const range = sameMonth
      ? `${isoMonday.getUTCDate()}–${end.getUTCDate()} ${MON[end.getUTCMonth()]}`
      : `${isoMonday.getUTCDate()} ${MON[isoMonday.getUTCMonth()]} – ${end.getUTCDate()} ${MON[end.getUTCMonth()]}`;
    // The year only when it is not the current one — it is noise the other 99% of the time.
    return end.getUTCFullYear() === now.getUTCFullYear() ? range : `${range} ${end.getUTCFullYear()}`;
  }

  // ── month: 2026-08 ──────────────────────────────────────────────────────
  const mo = s.match(/^(\d{4})-(\d{2})$/);
  if (mo) {
    const y = Number(mo[1]); const m = Number(mo[2]) - 1;
    const monthsAgo = (now.getUTCFullYear() - y) * 12 + (now.getUTCMonth() - m);
    if (monthsAgo === 0) return 'This month';
    if (monthsAgo === 1) return 'Last month';
    return y === now.getUTCFullYear() ? MON[m] : `${MON[m]} ${y}`;
  }

  // ── day: 2026-08-27 ─────────────────────────────────────────────────────
  const dy = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dy) {
    const d = new Date(Date.UTC(Number(dy[1]), Number(dy[2]) - 1, Number(dy[3])));
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const daysAgo = Math.round((+today - +d) / 86400000);
    if (daysAgo === 0) return 'Today';
    if (daysAgo === 1) return 'Yesterday';
    const base = `${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
    return d.getUTCFullYear() === now.getUTCFullYear() ? base : `${base} ${d.getUTCFullYear()}`;
  }

  return s;
};

/**
 * WHERE A PERSON ACTUALLY SITS — one producer, because a seat is three facts.
 *
 * The order page printed a bare "32". Seat 32 of what? The payload carried section 123 and row F
 * the whole time; the mapper only ever read `seat.seatNumber`, so an operator answering "where am
 * I sitting?" had a number that identified nothing — there is a seat 32 in every row of every
 * section in the stadium.
 *
 * Reads the STRUCTURED fields rather than the seat's own `name`, which is a plan-authored string
 * ("123 • F-32" on this venue, "A12" on another) and cannot be relied on to contain all three.
 * Any part that is genuinely absent is dropped rather than filled with a placeholder, so a
 * general-admission ticket says nothing instead of "Section — · Row — · Seat —".
 */
/**
 * THE GATE A HOLDER WALKS THROUGH — read off the TICKET, and only the ticket.
 *
 * `ScanItems.entranceId` is the authority. It is stamped at fulfilment from the seat's reservation
 * row for THIS event, and it is what the pass prints and what the person is told at the turnstile.
 *
 * `Section → Entrance` is NOT a fallback and must never be used for this: a Section is a VENUE
 * entity reused across every event held there, while the gate serving it is set per event — the
 * same block is Gate 5 for one match and Gate 2 for the next. Reading the section's entrance
 * yields a confidently-wrong gate, which is worse than no gate, because someone acts on it.
 * No ticket entrance means no gate to show; say nothing rather than guess.
 */
export const gateLabel = (item: any): string => {
  const name = item?.entrance?.name ?? null;
  return name == null || name === '' ? '' : `Gate ${name}`;
};

export const seatLabel = (seat: any): string => {
  if (!seat || typeof seat !== 'object') return '';
  const section = seat.row?.section?.name ?? seat.sectionName ?? seat.section?.name ?? null;
  const row = seat.row?.name ?? seat.rowName ?? null;
  const number = seat.seatNumber ?? seat.number ?? null;
  return [
    section != null && section !== '' ? `Section ${section}` : null,
    row != null && row !== '' ? `Row ${row}` : null,
    number != null && number !== '' ? `Seat ${number}` : null,
  ].filter(Boolean).join(' · ');
};

// A gap between two instants, said the way a person says it — the one formatter for every surface
// that states a wait (the order journey, the support queue).
export const humanGap = (fromIso: string | number | Date, toIso: string | number | Date): string | null => {
  const a = new Date(fromIso).getTime(), b = new Date(toIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  // Say it the way a person would say it out loud. Nobody reports "14.2 h" — they say
  // "14 hours". Precision only survives where it changes the meaning: seconds and
  // minutes carry the leftover unit (a 90-second wait is "1 min 30 sec", not "2 min"),
  // and above an hour the leading unit is what anyone actually remembers.
  const s = Math.round((b - a) / 1000);
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;
  // Round ONCE, into the smallest unit the phrase will actually show, then split by
  // integer division. Rounding the leftover on its own lets it fill a whole unit that
  // the leading number never hears about — which is how 8 days 23h56m came out as
  // "8 days 24 hours". Branching on the ROUNDED magnitudes closes the same carry at
  // each boundary, so 59m59s reads "1 hour" rather than "60 minutes".
  const split = (total: number, per: number, bigUnit: string, smallUnit: string, hideBelow = 0) => {
    const big = Math.floor(total / per), small = total % per;
    return small > hideBelow ? `${plural(big, bigUnit)} ${plural(small, smallUnit)}` : plural(big, bigUnit);
  };
  const mins = Math.round(s / 60), hours = Math.round(s / 3600), days = Math.round(s / 86400);
  if (s < 2) return 'moments';
  if (s < 60) return plural(s, 'second');
  // Seconds are precision only while the gap is small enough for them to mean something.
  // A 90-second wait really is "1 minute 30 seconds"; a 41-minute one is not "41 minutes
  // 46 seconds" — that's the same machine voice as "14.2 h", just spelled out. Past ten
  // minutes the seconds stop being information and become noise, so drop them; below it,
  // a stray handful under a minutes-scale gap is noise too.
  if (mins < 10) return split(s, 60, 'minute', 'second', 4);
  if (mins < 60) return plural(mins, 'minute');
  if (hours < 24) return split(mins, 60, 'hour', 'minute');
  if (days < 14) return split(hours, 24, 'day', 'hour');
  if (days < 60) return plural(days, 'day');
  if (days < 365) return plural(Math.round(days / 30), 'month');
  return plural(Math.round(days / 365), 'year');
};

/**
 * "1 person", "2 people", "1 recipient". Both forms are passed in because the nouns here are often
 * irregular — every `n === 1 ? '' : 's'` copy in this codebase would print "1 peoples" or "2 persons".
 * Found as "1 people" / "1 recipients" on a live one-person campaign.
 */
export const countNoun = (n: number, one: string, many: string): string =>
  `${Number(n || 0).toLocaleString()} ${Number(n) === 1 ? one : many}`;
